import * as React from "react";
import * as H from "history";
import { match } from "react-router";
import { toast } from "react-toastify";

import {
  FilterParams,
  ITerminology,
  PaginatedParams
} from "src/library/utils";
import { Table } from "src/core/dashboard/components/widgets";
import {
  IDrop,
  useDropsApi
} from "src/core/drops/api/drops.api";
import {
  IUser,
  UpdateUserParams,
  useUserApi
} from "src/core/users/api/users.api";
import { TableRefProps } from "src/core/dashboard/components/widgets/table/Table";
import { ActionButton } from "src/library/components/Button";
import { Icons } from "src/library/components/Icon/types";
import { getUserAssignableDropsTableColumns } from "src/core/shared/components/tables/userAssignableDropsTableColumn";

import { UrlParams } from "./CustomerUserDropAssignmentPage";
import { SelectableDrop } from "./CustomerUserAssignedDropsTab";

type TabProps = {
  customerId: number;
  user: IUser;
  history: H.History;
  terminology: ITerminology;
  match: match<UrlParams> | null;
  reloadUser: () => void;
};

const defaultPagination: PaginatedParams<IDrop> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "branchNumber", "DESC" ] ]
};

/**
 * Assignable drops to a user tab, displayed in a table
 *
 * @returns
 */
const CustomerUserDropsTab: React.ReactFCWithChildren<TabProps> = props => {
  const {
    terminology, match, user, reloadUser
  } = props;

  const TableRef = React.useRef<TableRefProps>(null);
  const [ updateUserResponse, updateUser ] = useUserApi("UPDATE_USER");
  const [ dropsResponse, getDrops ] = useDropsApi("LIST_USER_ASSIGNABLE_DROPS");
  const [ availableDrops, setAvailableDrops ] = React.useState<SelectableDrop[]>([]);
  const [ reloadUserFlag, setReloadUserFlag ] = React.useState(false);
  const [ loading, setLoading ] = React.useState(false);

  //getting the drops out of the response and mapping them to have a selected property
  React.useEffect(() => {
    setAvailableDrops(() => {
      const drops = dropsResponse.data?.result ? dropsResponse.data?.result as SelectableDrop[] : [];

      return drops.map(drop => ({
        ...drop,
        selected: false
      }));
    });
  }, [ dropsResponse.data?.result ]);

  //loading is set true by buttons too, this is so there is an instant reaction to interactions.
  React.useEffect(() => {
    if (dropsResponse.pending) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [ dropsResponse ]);

  //if any errors
  React.useEffect(() => {
    if (updateUserResponse.error) {
      toast.error(`Could not save ${terminology.user.singular.text}. Please try again.`);
    }

    if (dropsResponse.error) {
      toast.error(`Could not retrieve ${terminology.drop.plural.text}. Please try again`);
    }
  }, [
    terminology,
    updateUserResponse.error,
    dropsResponse.error
  ]);

  const onFetchData = React.useCallback(
    (params: {filters: FilterParams<IDrop>, pagination: PaginatedParams<IDrop>}) => {
      getDrops({
        userId: user.id,
        pagination: params.pagination,
        filters: params.filters
      });
    },
    [ getDrops, user ]
  );

  React.useEffect(() => {
    if (updateUserResponse.complete) {
      if (reloadUserFlag) { //only reload user if we said to
        reloadUser();
        setReloadUserFlag(false);
      }
    }
    //only want to trigger this when the response is updated not the flag
  }, [ reloadUser, updateUserResponse ]);

  const handleUpdateUser = React.useCallback(() => {
    setAvailableDrops(prevDrops => {
      if (!user || !user.userBranches) {
        return prevDrops;
      }

      if (prevDrops.length < 1) {
        toast.error(`Please select at least 1 ${terminology.drop.singular.text}`);

        return prevDrops;
      }

      const dropsToAssign = prevDrops.filter(drop => drop.selected === true);

      updateUser({
        ...user,
        userBranches: [ ...dropsToAssign, ...user.userBranches ]
      } as UpdateUserParams);
      setReloadUserFlag(true);
      setLoading(true);

      return prevDrops.map(drop => ({
        ...drop,
        selected: false
      }));
    });
    // Return if there are no user.userBranches
  }, [
    user,
    updateUser,
    terminology
  ]);

  const columns = React.useMemo(() => {
    let isDropSelected = false;

    return getUserAssignableDropsTableColumns({
      checkboxAction: (drop: IDrop) => {
        setAvailableDrops(prevDrops => {
          const dropExists = prevDrops.findIndex(selDrop => selDrop.id === drop.id);
          const newDrops = [ ...prevDrops ];

          newDrops[ dropExists ].selected = !newDrops[ dropExists ].selected;
          isDropSelected = newDrops.filter(drop => drop.selected).length ? true : false;

          return newDrops;
        });
      },
      bulkActionButton: () => {
        return (
          <ActionButton
            title="Bulk Actions"
            items={[
              {
                title: "Give Access",
                icon: Icons.TICK,
                disabled: !isDropSelected,
                action: handleUpdateUser
              },
              {
                title: "Select All",
                action: () => {
                  setAvailableDrops(prevDrops => {
                    const selectedDrops = prevDrops.filter(drop => drop.selected === true);

                    if (selectedDrops.length === prevDrops.length) {
                      isDropSelected = false;

                      return prevDrops.map(drop => ({
                        ...drop,
                        selected: false
                      }));
                    }

                    isDropSelected = true;

                    return prevDrops.map(drop => ({
                      ...drop,
                      selected: true
                    }));
                  });
                }
              }
            ]}
          />
        );
      },
      terminology
    });
  }, [ terminology, handleUpdateUser ]);

  return (
    <Table
      ref={TableRef}
      tableOf={`${user.firstName} can not access the following ${terminology.drop.plural.text}`}
      error={dropsResponse.error}
      loading={loading}
      data={dropsResponse.data?.result && !loading ? availableDrops : []}
      onFetchData={onFetchData}
      defaultPagination={defaultPagination}
      pageCount={dropsResponse.data?.pages ? dropsResponse.data.pages : 0}
      rowCount={dropsResponse.data?.count ? dropsResponse.data.count : 0}
      columns={columns}
    />
  );
};

export default CustomerUserDropsTab;
